<template>
  <div id="cesium-container" ref="cesiumContainer">
    <div id="blurred-background" v-if="isViewerLoading || loading">
      <v-progress-circular
        id="loading-indicator"
        :size="40"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>
    <cesium-recorder ref="cesiumRecorder" />
    <cesium-lighting-button ref="cesiumLightingButton" />
    <OverlayComponent
      v-if="dates.length != 0"
      :elementToOverlay="timelineContainer"
      ><CesiumMarkerOverlay
        :startDate="dates[0]"
        :endDate="dates[dates.length - 1]"
        :markers="markers"
        v-on:changeDate="setTimeFromUTC($event)"
    /></OverlayComponent>
  </div>
</template>

<script>
import { CesiumInitialization } from '../../components/ThreeD/CesiumInitialization'
import { AnalyticalOrbit } from '../../components/ThreeD/AnalyticalOrbit'
import CesiumRecorder from '../../components/ThreeD/CesiumRecorder.vue'
import CesiumLightingButton from '../../components/ThreeD/CesiumLightingButton.vue'
import CesiumTools from '../../components/ThreeD/CesiumTools'
import CesiumConstellationsTools from '../../components/ThreeD/CesiumConstellationsTools'
import * as Cesium from 'cesium'
import CesiumMarkerOverlay from './CesiumMarkerOverlay'
import OverlayComponent from './OverlayComponent'
import { mapGetters } from 'vuex'

export default {
  name: 'EvasiveManeuverCesiumVisualizer',
  mixins: [CesiumInitialization, AnalyticalOrbit],
  props: {
    loading: { type: Boolean, default: false },
    satellitePositions: {
      type: Array,
      required: true
    },
    dates: {
      type: Array,
      required: true
    },
    maneuverDates: {
      type: Array,
      default: () => []
    }
  },
  components: {
    'cesium-recorder': CesiumRecorder,
    'cesium-lighting-button': CesiumLightingButton,
    CesiumMarkerOverlay: CesiumMarkerOverlay,
    OverlayComponent: OverlayComponent
  },
  data() {
    return {
      isViewerLoading: false,
      satellitesDS: null,
      lineDS: null,
      timelineContainer: null
    }
  },
  computed: {
    ...mapGetters({}),
    markers() {
      const satelliteMarkers = this.maneuverDates.map((date) => ({
        color: 'blue',
        tooltip: 'Satellite burn',
        date: date
      }))

      return satelliteMarkers
    },
    julianDates() {
      return this.dates.map((sampleTime) =>
        Cesium.JulianDate.fromIso8601(sampleTime)
      )
    },
    julianStartDate() {
      return Cesium.JulianDate.fromIso8601(this.dates[0])
    },
    julianEndDate() {
      return Cesium.JulianDate.fromIso8601(this.dates[this.dates.length - 1])
    }
  },
  watch: {
    async satellitePositions() {
      await this.resetSatellites()
      await this.resetLines()
    },
    async dates() {
      await this.resetSatellites()
      await this.resetLines()
    }
  },
  async mounted() {
    this.isViewerLoading = true
    this.initializeViewer(true, true, false, false, false, true)
    this.insertElementInToolbar(this.$refs.cesiumLightingButton.$el)
    this.insertElementInToolbar(this.$refs.cesiumRecorder.$el)

    this.satellitesDS = new Cesium.CustomDataSource()
    this.viewer.dataSources.add(this.satellitesDS)
    this.lineDS = new Cesium.CustomDataSource()
    this.viewer.dataSources.add(this.lineDS)

    this.viewer.clock.shouldAnimate = true
    this.setCesiumClock(this.julianStartDate, this.julianEndDate)
    this.viewer.clock.onTick.addEventListener(this.computeIcrfToFixed)
    this.computeIcrfToFixed(this.viewer.clock, true)
    this.viewer.scene.render(this.viewer.clock.currentTime)
    this.initSatellites()
    this.initLines()

    this.timelineContainer = this.viewer.timeline.container
    this.isViewerLoading = false
  },
  beforeDestroy() {},
  methods: {
    initMarkers() {
      const { x, y, width, height } =
        this.viewer.timeline.container.getBoundingClientRect()
      const containerRect = this.$refs.cesiumContainer?.getBoundingClientRect()
      if (containerRect) {
        const x2 = containerRect.x,
          y2 = containerRect.y
        this.posX = x - x2
        this.posY = y - y2
        this.width = width
        this.height = height
      }
    },
    initSatellites() {
      const satellite = this.satellitesDS.entities.add(
        this.initSatEntity(
          this.satellitePositions,
          this.julianDates,
          'SATELLITE',
          Cesium.Color.BLUE
        )
      )
    },
    resetSatellites() {
      this.isViewerLoading = true
      this.satellitesDS.entities.removeAll()
      this.initSatellites()

      this.setCesiumClock(this.julianStartDate, this.julianEndDate)
      this.isViewerLoading = false
    },
    initLines() {
      this.lineDS.entities.add({
        polyline: {
          positions: this.julianDates.map((date) =>
            this.satellitesDS.entities
              .getById('SATELLITE')
              ?.position?.getValue(date)
          ),
          width: 2,
          material: Cesium.Color.RED
        }
      })
    },
    resetLines() {
      this.isViewerLoading = true
      this.lineDS.entities.removeAll()
      this.initLines()
      this.setCesiumClock(this.julianStartDate, this.julianEndDate)
      this.isViewerLoading = false
    },
    initSatEntity(satPositions, sampleTimes, satName, color) {
      const sampledPositions = this.createPositionsProperty(
        satPositions,
        sampleTimes,
        Cesium.ReferenceFrame.INERTIAL
      )

      return new Cesium.Entity({
        id: satName,
        description: satName,
        position: sampledPositions,
        show: true,
        point: {
          pixelSize: 8,
          color: color,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 1
        },
        label: {
          text: satName,
          font: '16px Helvetica',
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
          pixelOffset: new Cesium.Cartesian2(0, 20),
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          distanceDisplayCondition: new Cesium.DistanceDisplayCondition(
            5e4,
            3e7
          )
        }
      })
    },

    createPositionsProperty(
      positions,
      sampleTimes,
      referenceFrame = Cesium.ReferenceFrame.FIXED
    ) {
      const sampledPositionProperty = new Cesium.SampledPositionProperty(
        referenceFrame
      )
      sampledPositionProperty.setInterpolationOptions(
        Cesium.LagrangePolynomialApproximation,
        5
      )

      sampledPositionProperty.addSamples(
        sampleTimes,
        positions.map(
          (position) =>
            new Cesium.Cartesian3(position[0], position[1], position[2])
        )
      )

      return sampledPositionProperty
    },

    setTimeFromUTC(date) {
      this.viewer.clock.currentTime = Cesium.JulianDate.fromIso8601(date)
    }
  }
}
</script>

<style scoped>
#cesium-container {
  position: relative;
  height: 500px;
  width: 100%;
  z-index: 2;
}

#blurred-background {
  background-color: black;
  opacity: 0.5;
  height: 100%;
  width: 100%;
  position: absolute;
  z-index: 3;
}

#loading-indicator {
  position: absolute;
  margin: auto;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 4;
  display: block;
}

.infobox {
  display: block;
  position: absolute;
  left: 20px;
  top: 20px;
  z-index: 9;
  background: white;
  color: blue;
  padding: 4px;
  border: 2px solid red;
  border-radius: 5px;
}
</style>
